import { effectiveAge, resolveSchoolYear } from "./age.js";
import { resolveRungByYear, sanitizeYears } from "./ladder.js";
import { cmsWordlistCategories, cmsWordsForCategory } from "./content.js";
// Starter spelling lists + weekly auto-serve for the Learn & Earn Spelling test (R3a).
//
// Why this exists: spelling used to depend ENTIRELY on a parent typing words in and
// re-ticking them every week (the payout flow unticks everything — see App.jsx's
// onLearnEarn). Forget once and the game quietly dies. These built-in lists auto-serve a
// fresh batch each week with zero parent effort; a parent's own ticked words always take
// priority (auto-serve pauses entirely while ANY custom word is ticked — see ChildView.jsx's
// LearnEarnTab), so existing custom-list families see no behaviour change until their list
// runs dry.
//
// Word lists: the UK Department for Education statutory spelling lists from the national
// curriculum (Years 1–2 common exception words, and the Year 3–4 and Year 5–6 statutory
// lists). Contains public sector information licensed under the Open Government Licence
// v3.0 (https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/).
// Single-letter words ("a", "I") and title abbreviations ("Mr", "Mrs") are omitted — they
// make no sense read aloud by the speech engine as a spelling prompt. Casing is kept as
// printed (e.g. "February", "Christmas"); SpellingTest's check() compares case-insensitively.
//
// Serving is DETERMINISTIC and stateless, mirroring lib/dailyQuestion.js's philosophy: the
// week's batch is a pure function of (list, week, batch size), so nothing is written to KV
// to rotate words and every device computes the same batch. The batch walks the list
// circularly week by week, so over enough weeks every word gets served. (Devices in
// different timezones could in principle disagree near a week boundary — same household,
// same timezone is the overwhelmingly common case, and the disagreement self-heals within
// the week.)

export const SPELLING_LISTS = {
  y12: {
    label: "Year 1–2",
    minAge: 4,
    maxAge: 7,
    words: [
      "the", "do", "to", "today", "of", "said", "says", "are", "were", "was", "is", "his",
      "has", "you", "your", "they", "be", "he", "me", "she", "we", "no", "go", "so", "by",
      "my", "here", "there", "where", "love", "come", "some", "one", "once", "ask", "friend",
      "school", "put", "push", "pull", "full", "house", "our", "door", "floor", "poor",
      "because", "find", "kind", "mind", "behind", "child", "children", "wild", "climb",
      "most", "only", "both", "old", "cold", "gold", "hold", "told", "every", "everybody",
      "even", "great", "break", "steak", "pretty", "beautiful", "after", "fast", "last",
      "past", "father", "class", "grass", "pass", "plant", "path", "bath", "hour", "move",
      "prove", "improve", "sure", "sugar", "eye", "could", "should", "would", "who", "whole",
      "any", "many", "clothes", "busy", "people", "water", "again", "half", "money",
      "parents", "Christmas",
    ],
  },
  y34: {
    label: "Year 3–4",
    minAge: 8,
    maxAge: 9,
    words: [
      "accident", "accidentally", "actual", "actually", "address", "answer", "appear",
      "arrive", "believe", "bicycle", "breath", "breathe", "build", "business",
      "calendar", "caught", "centre", "century", "certain", "circle", "complete",
      "consider", "continue", "decide", "describe", "different", "difficult", "disappear",
      "early", "earth", "eight", "eighth", "enough", "exercise", "experience",
      "experiment", "extreme", "famous", "favourite", "February", "forward", "forwards",
      "fruit", "grammar", "group", "guard", "guide", "heard", "heart", "height", "history",
      "imagine", "increase", "important", "interest", "island", "knowledge", "learn",
      "length", "library", "material", "medicine", "mention", "minute", "natural",
      "naughty", "notice", "occasion", "occasionally", "often", "opposite", "ordinary",
      "particular", "peculiar", "perhaps", "popular", "position", "possess", "possession",
      "possible", "potatoes", "pressure", "probably", "promise", "purpose", "quarter",
      "question", "recent", "regular", "reign", "remember", "sentence", "separate",
      "special", "straight", "strange", "strength", "suppose", "surprise", "therefore",
      "though", "although", "thought", "through", "various", "weight", "woman", "women",
    ],
  },
  y56: {
    label: "Year 5–6",
    minAge: 10,
    maxAge: 99,
    words: [
      "accommodate", "accompany", "according", "achieve", "aggressive", "amateur",
      "ancient", "apparent", "appreciate", "attached", "available", "average", "awkward",
      "bargain", "bruise", "category", "cemetery", "committee", "communicate",
      "community", "competition", "conscience", "conscious", "controversy",
      "convenience", "correspond", "criticise", "curiosity", "definite", "desperate",
      "determined", "develop", "dictionary", "disastrous", "embarrass", "environment",
      "equip", "equipped", "equipment", "especially", "exaggerate", "excellent",
      "existence", "explanation", "familiar", "foreign", "forty", "frequently",
      "government", "guarantee", "harass", "hindrance", "identity", "immediate",
      "immediately", "individual", "interfere", "interrupt", "language", "leisure",
      "lightning", "marvellous", "mischievous", "muscle", "necessary", "neighbour",
      "nuisance", "occupy", "occur", "opportunity", "parliament", "persuade", "physical",
      "prejudice", "privilege", "profession", "programme", "pronunciation", "queue",
      "recognise", "recommend", "relevant", "restaurant", "rhyme", "rhythm", "sacrifice",
      "secretary", "shoulder", "signature", "sincere", "sincerely", "soldier", "stomach",
      "sufficient", "suggest", "symbol", "system", "temperature", "thorough", "twelfth",
      "variety", "vegetable", "vehicle", "yacht",
    ],
  },
};

export const SPELLING_LIST_ORDER = ["y12", "y34", "y56"];

// CMS-aware view of the spelling lists (docs/CMS_SCOPE.md §4.3). If any spelling
// categories have been published in the Content Studio, those are used exclusively —
// SPELLING_LISTS above becomes dead code for a household on that build until the CMS
// again has nothing published (which shouldn't normally happen once seeded). Until
// then, this returns SPELLING_LISTS completely unchanged, so nothing about today's
// behaviour changes until a category is actually published. Recomputed on every call
// rather than cached — cheap (a filter over an already-in-memory array), and always
// reflects whatever loadContentBundle() most recently fetched.
export function effectiveSpellingLists() {
  const cmsCats = cmsWordlistCategories("spelling");
  if (!cmsCats) return SPELLING_LISTS;
  const out = {};
  for (const cat of cmsCats) {
    // BUG FOUND AND FIXED (2026-08-20, real testing — Alan: "the Say As isn't voiced"):
    // this used to reduce every word down to a bare string (`.map((w) => w.word)`),
    // discarding everything else on the CMS entry INCLUDING speakAs before it ever
    // reached the app. The server (Audio tab, actual generation) reads published entries
    // directly and always saw speakAs correctly — this function is the ONE place in the
    // whole spelling pipeline that silently dropped it on the way to the child-facing
    // app, which is why the CMS could correctly show "With audio" for a word's speakAs
    // hash while the app itself never even knew that word had one. Keeping the object
    // (not just the string) is what actually closes this — autoSpellingBatch below reads
    // .word and .speakAs off it the same way a household's own custom words already do.
    const words = cmsWordsForCategory("spelling-word", cat.id)
      .filter((w) => w && typeof w.word === "string")
      .map((w) => ({ word: w.word, speakAs: typeof w.speakAs === "string" ? w.speakAs : undefined, description: typeof w.description === "string" ? w.description : undefined, grade: Number.isInteger(w.grade) ? w.grade : undefined, topic: typeof w.topic === "string" ? w.topic : undefined })); // grade/topic: Adaptive (LB-19)
    out[cat.id] = { label: cat.label, minAge: cat.minAge ?? 0, maxAge: cat.maxAge ?? 99, years: sanitizeYears(cat.years), words };
  }
  return out;
}

export function effectiveSpellingListOrder() {
  const cmsCats = cmsWordlistCategories("spelling");
  return cmsCats ? cmsCats.map((c) => c.id) : SPELLING_LIST_ORDER;
}

// Default words served per week when auto-serve is on. Deliberately below the manual
// SPELLING_CAP (15, LearningAdminTab.jsx) — a parent can raise it to that ceiling.
// LB-284 (Alan, 14 Sep: "Spellings is only 10 words - this default is too low"). 15 matches
// Coach's own test size and vocab; every other game defaulted higher than 10.
export const SPELLING_AUTO_DEFAULT_BATCH = 15;
export const SPELLING_AUTO_MAX_BATCH = 20; // Alan, 11 Sep: 15 → 20 words per week from a curriculum list

// Picks a list for a child — same shape/fallback behaviour as resolveVocabCategory in
// lib/vocab.js and resolveMathsBand in lib/maths.js. "off" is a valid explicit choice
// (auto-serve stays enabled in config but serves nothing) and is returned as-is.
export function resolveSpellingList(child, override) {
  if (override === "off") return "off";
  const lists = effectiveSpellingLists();
  const order = effectiveSpellingListOrder();
  if (override && override !== "auto" && lists[override]) return override;
  // Difficulty Ladder (docs/DIFFICULTY_LADDER_SCOPE.md): identical insertion point and
  // semantics as resolveMathsBand — school year beats age once year-keyed lists exist,
  // null falls through to the untouched age path (no CMS import yet, no resolvable
  // year, or an in-range content gap). Hardcoded y12/y34/y56 never carry years, so
  // nothing changes until Alan's import — which, for spelling, REPLACES the whole
  // collection (effectiveSpellingLists' CMS semantics), so the import file must ship
  // the complete per-year ladder, not a partial one.
  const byYear = resolveRungByYear(lists, order, resolveSchoolYear(child));
  if (byYear) return byYear;
  const age = effectiveAge(child);
  if (age === null) return order.includes("y34") ? "y34" : order[0] || "y34";
  const found = order.find((k) => lists[k] && age >= lists[k].minAge && age <= lists[k].maxAge);
  if (found) return found;
  if (!order.length) return "y34";
  return age < (lists[order[0]]?.minAge ?? 0) ? order[0] : order[order.length - 1];
}

// Stable integer index for a week, from the Monday-start week string weekStartOf() produces.
// Pure and clock-free beyond the date itself — see the determinism note in the header.
export function spellingWeekIndex(weekStartStr) {
  const ms = new Date(`${weekStartStr}T00:00:00`).getTime();
  if (Number.isNaN(ms)) return 0;
  return Math.floor(ms / (7 * 24 * 60 * 60 * 1000));
}

// This week's auto-served batch for a list: a circular slice that advances by batchSize
// each week. Returns SpellingTest-shaped word objects ({ id, word }); ids are prefixed
// "auto:" so nothing here can ever collide with a stored custom word's uid.
// Every word of a list as test items (Adaptive draws from the whole band, not a weekly slice).
export function spellingListItems(listKey) {
  const list = effectiveSpellingLists()[listKey];
  if (!list) return [];
  return list.words.map((w) => { const word = typeof w === "string" ? w : w.word; const item = { id: `auto:${word}`, word }; if (typeof w !== "string") { if (w.speakAs) item.speakAs = w.speakAs; if (typeof w.description === "string" && w.description) item.description = w.description; if (Number.isInteger(w.grade)) item.grade = w.grade; if (w.topic) item.topic = w.topic; } return item; });
}
// The curriculum item a parent-typed word matches, or null (LB-278). A parent-typed spelling
// word is `{ id, word, testInclude }` and carries no grade, topic or band of its own — those
// exist only on curriculum list items — so "is this word in the child's year band" can only be
// answered by finding the word in the lists. Alan, 14 Sep: "surely you can look up all CMS
// lists to check for the presence of an exact match?"
//
// A word in more than one list resolves to the EARLIEST band that holds it: a word taught in
// Year 3 and still listed in Year 5 is a Year 3 word, and taking the later one would let an
// easy word count as at-band for an older child.
//
// Matching is exact after case-folding and trimming. Deliberately not fuzzy: a near miss would
// put a word the child has never been taught into their adaptive practice, and credit its
// topic towards a band move.
export function curriculumItemForWord(word, bandOrder = null) {
  const needle = String(word || "").trim().toLowerCase();
  if (!needle) return null;
  const lists = effectiveSpellingLists();
  const order = bandOrder && bandOrder.length ? bandOrder : effectiveSpellingListOrder();
  for (const key of order) {
    const list = lists[key];
    if (!list || !Array.isArray(list.words)) continue;
    for (const w of list.words) {
      const text = typeof w === "string" ? w : w && w.word;
      if (text && String(text).trim().toLowerCase() === needle) {
        return {
          band: key,
          word: needle,
          grade: typeof w === "object" && Number.isInteger(w.grade) ? w.grade : undefined,
          topic: typeof w === "object" && typeof w.topic === "string" ? w.topic : undefined,
        };
      }
    }
  }
  return null;
}

export function autoSpellingBatch(listKey, weekStartStr, batchSize) {
  const list = effectiveSpellingLists()[listKey];
  if (!list || !Array.isArray(list.words) || list.words.length === 0) return [];
  const rawSize = parseInt(batchSize, 10);
  const size = Math.max(1, Math.min(SPELLING_AUTO_MAX_BATCH, Number.isNaN(rawSize) ? SPELLING_AUTO_DEFAULT_BATCH : rawSize));
  const words = list.words;
  // Normalizes either shape list.words can hold: a plain string (SPELLING_LISTS, the
  // hardcoded fallback used when nothing's published — words there never carry speakAs)
  // or a {word, speakAs} object (effectiveSpellingLists' CMS-derived branch, since its
  // own fix above). Handling both here, in the one place that turns a list entry into
  // the object SpellingTest actually consumes, is what makes the fix above actually
  // reach the app — this function is that hand-off point.
  const toBatchItem = (w) => {
    const word = typeof w === "string" ? w : w.word;
    const speakAs = typeof w === "string" ? undefined : w.speakAs;
    const item = { id: `auto:${word}`, word };
    if (speakAs) item.speakAs = speakAs;
    if (typeof w !== "string" && typeof w.description === "string" && w.description) item.description = w.description;
    if (typeof w !== "string") { if (Number.isInteger(w.grade)) item.grade = w.grade; if (w.topic) item.topic = w.topic; }
    return item;
  };
  if (size >= words.length) return words.map(toBatchItem);
  const start = (spellingWeekIndex(weekStartStr) * size) % words.length;
  const batch = [];
  for (let i = 0; i < size; i++) batch.push(words[(start + i) % words.length]);
  return batch.map(toBatchItem);
}
